import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackLogin } from '../services/analytics';
import { jwtDecode } from 'jwt-decode';
import '../styles/v2.css';

export default function LoginV2() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/login', { username, password });
      const { token, role } = response.data;
      let decoded = null;
      try { decoded = jwtDecode(token); } catch {}
      localStorage.setItem('token', token);
      localStorage.setItem('role', role);
      localStorage.setItem('isLoggedIn', 'true');
      // shop_name comes back in the login response; store before trackLogin so
      // PostHog identify/group + Firebase user property pick it up.
      localStorage.setItem('shop_name', response.data.shop_name || 'Unknown');
      const userId = decoded?.id || decoded?.userId || decoded?.user_id || 'unknown';
      trackLogin(userId, username, role);
      navigate('/new/dashboard');
    } catch {
      setError('Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="v2-login-root">

      {/* ── Left brand panel (desktop only) ── */}
      <div className="v2-login-left v2-mesh-bg" style={{ width: '58%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 56, overflow: 'hidden', position: 'relative' }}>

        {/* Decorative concentric rings */}
        {[640, 460, 280].map((size, i) => (
          <div key={i} style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: size, height: size, borderRadius: '50%',
            border: `1px solid rgba(16,185,129,${0.1 - i * 0.02})`,
            pointerEvents: 'none',
          }} />
        ))}
        {/* Giant watermark */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -55%)', fontSize: 320, fontWeight: 900, color: 'rgba(255,255,255,0.018)', lineHeight: 1, pointerEvents: 'none', userSelect: 'none', letterSpacing: '-0.06em' }}>
          ZS
        </div>

        {/* Logo + headline */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56 }}>
            <div style={{ width: 44, height: 44, borderRadius: 16, background: 'linear-gradient(135deg,#34d399,#059669)', boxShadow: '0 8px 24px rgba(16,185,129,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontWeight: 900, fontSize: 15 }}>ZS</span>
            </div>
            <div>
              <p style={{ color: 'white', fontWeight: 700, fontSize: 17, lineHeight: 1, letterSpacing: '-0.02em' }}>ZambiaShoppe</p>
              <p style={{ color: 'rgba(16,185,129,0.6)', fontSize: 11, fontWeight: 500, marginTop: 3 }}>Inventory Management Platform</p>
            </div>
          </div>
          <h1 style={{ fontSize: 50, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.03em', marginBottom: 20 }}>
            <span className="v2-gradient-headline">The smartest way<br />to run your shop.</span>
          </h1>
          <p style={{ color: 'rgba(209,250,229,0.45)', fontSize: 16, lineHeight: 1.65, maxWidth: 320 }}>
            Real-time inventory, AI-powered product recognition, and business insights — all in one beautiful platform.
          </p>
        </div>

        {/* Floating stat cards */}
        <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 400, height: 280 }}>

            {/* Card A: Weekly Sales */}
            <div className="v2-glass v2-float-a" style={{ position: 'absolute', top: 0, right: 0, borderRadius: 16, padding: 20, width: 210 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(16,185,129,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg style={{ width: 14, height: 14, color: '#34d399' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                    </svg>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 500 }}>Weekly Sales</span>
                </div>
                <span style={{ color: 'rgba(16,185,129,0.7)', fontSize: 10, fontWeight: 500 }}>Live</span>
              </div>
              <p style={{ color: 'white', fontSize: 24, fontWeight: 900, letterSpacing: '-0.03em' }}>K 12,450</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <span style={{ color: '#34d399', fontSize: 12, fontWeight: 700 }}>↑ 12%</span>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>vs last week</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginTop: 12, height: 24 }}>
                {[50, 65, 45, 80, 60, 100, 30].map((h, i) => (
                  <div key={i} style={{ flex: 1, borderRadius: 2, background: i === 5 ? 'rgba(52,211,153,0.8)' : 'rgba(16,185,129,0.25)', height: `${h}%` }} />
                ))}
              </div>
            </div>

            {/* Card B: Stock Alert */}
            <div className="v2-glass v2-float-b" style={{ position: 'absolute', top: 90, left: 0, borderRadius: 16, padding: 20, width: 185 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg style={{ width: 14, height: 14, color: '#fbbf24' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 500 }}>Stock Alerts</span>
              </div>
              <p style={{ color: 'white', fontSize: 24, fontWeight: 900, letterSpacing: '-0.03em' }}>8 <span style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.35)' }}>items</span></p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <div className="v2-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbf24' }} />
                <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600 }}>Needs reorder</span>
              </div>
              <div style={{ marginTop: 12, height: 6, borderRadius: 99, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '35%', borderRadius: 99, background: '#fbbf24' }} />
              </div>
            </div>

            {/* Card C: Profit */}
            <div className="v2-glass v2-float-c" style={{ position: 'absolute', bottom: 0, right: 24, borderRadius: 16, padding: 20, width: 192 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(139,92,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg style={{ width: 14, height: 14, color: '#c084fc' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                  </svg>
                </div>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 500 }}>Weekly Profit</span>
              </div>
              <p style={{ color: 'white', fontSize: 24, fontWeight: 900, letterSpacing: '-0.03em' }}>K 3,200</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                <span style={{ color: '#34d399', fontSize: 12, fontWeight: 700 }}>↑ 8%</span>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>margin improving</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginTop: 12, height: 20 }}>
                {[55, 70, 60, 85, 100, 75, 40].map((h, i) => (
                  <div key={i} style={{ flex: 1, borderRadius: 2, background: i === 4 ? 'rgba(192,132,252,0.9)' : 'rgba(139,92,246,0.25)', height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom trust bar */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(16,185,129,0.25),transparent)', marginBottom: 24 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex' }}>
                {['#34d399,#059669', '#60a5fa,#3b82f6', '#c084fc,#9333ea'].map((grad, i) => (
                  <div key={i} style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid #04100a', background: `linear-gradient(135deg,${grad})`, marginLeft: i > 0 ? -8 : 0 }} />
                ))}
              </div>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Trusted by shop owners across Zambia 🇿🇲</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="v2-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ color: 'rgba(16,185,129,0.55)', fontSize: 11, fontWeight: 500 }}>All systems operational</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f7faf7', overflowY: 'auto', padding: '48px 32px', position: 'relative' }}>

        {/* Dot grid */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle,rgba(0,0,0,0.04) 1px,transparent 1px)', backgroundSize: '22px 22px', opacity: 0.8, pointerEvents: 'none' }} />

        <div style={{ width: '100%', maxWidth: 390, position: 'relative' }}>

          {/* Mobile logo */}
          <div className="v2-mobile-logo" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 40 }}>
            <div style={{ width: 40, height: 40, borderRadius: 16, background: 'linear-gradient(135deg,#34d399,#059669)', boxShadow: '0 6px 20px rgba(16,185,129,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontWeight: 900, fontSize: 14 }}>ZS</span>
            </div>
            <span style={{ color: '#0f172a', fontWeight: 700, fontSize: 18 }}>ZambiaShoppe</span>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 36 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 99, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.18)', marginBottom: 20 }}>
              <div className="v2-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ color: '#065f46', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}>Secure sign-in</span>
            </div>
            <h2 style={{ color: '#0f172a', fontSize: 30, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 10 }}>Welcome back 👋</h2>
            <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6 }}>
              Sign in to your ZambiaShoppe account to manage your inventory and sales.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Username */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Username</label>
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#cbd5e1', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                <input
                  className="v2-input"
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Password</label>
                <a href="/new/forgot-password" style={{ fontSize: 13, color: '#10b981', fontWeight: 600, textDecoration: 'none' }}
                   onMouseEnter={e => e.target.style.color = '#059669'} onMouseLeave={e => e.target.style.color = '#10b981'}>
                  Forgot password?
                </a>
              </div>
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#cbd5e1', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
                <input
                  className="v2-input v2-input-pr"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}
                >
                  {showPassword ? (
                    <svg style={{ width: 17, height: 17 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg style={{ width: 17, height: 17 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ padding: '12px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: 13, fontWeight: 500 }}>
                {error}
              </div>
            )}

            <button type="submit" className="v2-btn-submit" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? 'Signing in…' : 'Sign in to ZambiaShoppe'}
            </button>
          </form>

          {/* Divider */}
          <div style={{ position: 'relative', margin: '28px 0' }}>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '100%', borderTop: '1px solid #e2e8f0' }} />
            </div>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              <span style={{ background: '#f7faf7', padding: '0 12px', color: '#94a3b8', fontSize: 12, fontWeight: 500 }}>Don't have an account?</span>
            </div>
          </div>

          <a href="/new/register" className="v2-btn-register">
            <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
            </svg>
            Create your free account →
          </a>

          <p style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 11, marginTop: 56, letterSpacing: '0.04em' }}>
            ZambiaShoppe · Secure &amp; encrypted
          </p>
        </div>
      </div>
    </div>
  );
}
